import { Elements } from '@stripe/react-stripe-js';
import { useNavigate } from '@tanstack/react-location';
import {
  PaymentClientSecret,
  PaymentMethodTypeEnum,
  CreatePaymentIntentBody,
  SetupFutureUsage,
  createPaymentIntent,
  getPaymentClientSecretWithAuthToken,
} from '@frontend/api-weave-pay';
import { stripeAppearance as appearance, useStripePromise } from '@frontend/payments-stripe-wrapper';
import { ContentLoader } from '@frontend/design-system';
import { usePaymentRequest } from '../../hooks';
import { Payment } from '../../types';
import { convertToCents } from '../../utils/currency';
import { PaymentPortal } from '../PaymentPortal';

export const WeavePay = () => {
  const navigate = useNavigate();
  const { stripePromise } = useStripePromise();

  const {
    paymentRequest: requestForPayment,
    loading: initialLoad,
    windowLocationChange: windowLocationLoad,
    refetchData,
  } = usePaymentRequest();
  const loading = initialLoad || windowLocationLoad;

  // Info: Set Payment Intent
  const getClientSecret = async (payment: Payment) => {
    const url = requestForPayment?.payments || '';
    const v2Url = url.replace('payments/', 'payments/v2/');
    const requestBody: CreatePaymentIntentBody = {
      amount: convertToCents(payment?.amount || 0),
      cardholderName: payment?.cardholderName || '',
      origin: 'PAYMENT_PORTAL',
      person: {
        id: requestForPayment?.personId,
        email: payment?.receiptEmail || '',
        firstName: payment.patientName?.firstName,
        lastName: payment.patientName?.lastName,
      },
      paymentId: requestForPayment?.paymentId,
      paymentMethodType: payment?.paymentMethodType,
      setupFutureUsage:
        payment?.setupFutureUsage &&
        payment.paymentMethodType !== PaymentMethodTypeEnum.AFFIRM &&
        (requestForPayment?.enableSavingAch || payment.paymentMethodType !== PaymentMethodTypeEnum.ACH)
          ? SetupFutureUsage.offSession
          : null,
    };

    let clientSecret;
    try {
      const {
        paymentToken,
        links: { self: paymentIntentUrl },
      } = await createPaymentIntent({ url: v2Url, requestBody });

      try {
        const clientSecretResponse: PaymentClientSecret = await getPaymentClientSecretWithAuthToken(
          paymentIntentUrl,
          paymentToken
        );
        clientSecret = clientSecretResponse.paymentIntent.clientSecret;
      } catch (err) {
        navigate({ to: '/error' });
      }
    } catch (err) {
      navigate({ to: '/error' });
    }
    return { clientSecret };
  };

  if (loading || !requestForPayment?.requestId) {
    return <ContentLoader />;
  }
  return (
    <Elements
      stripe={stripePromise || null}
      options={{
        amount: convertToCents(requestForPayment?.amount || 50),
        setupFutureUsage: undefined,
        mode: 'payment',
        currency: 'usd',
        appearance,
        onBehalfOf: requestForPayment?.onBehalfOf,
        paymentMethodOptions: {
          us_bank_account: {
            financial_connections: {
              permissions: ['payment_method', 'balances'],
            },
          },
        },
      }}
    >
      <PaymentPortal
        setupPaymentIntent={getClientSecret}
        isTTP
        requestForPayment={requestForPayment}
        requestedAmount={requestForPayment?.amount}
        refetchData={refetchData}
      />
    </Elements>
  );
};
